<script setup lang="ts">
import EventRegistrationForm from '~/components/agenda/EventRegistrationForm.vue'
import AddToCalendar from '~/components/agenda/AddToCalendar.vue'

const route = useRoute()
const { getEventBySlug, getUpcomingEvents } = useAgenda()
const { setEventSeo } = useSeo()
const { getMapsUrl } = useMaps()
const event = await getEventBySlug(route.params.slug as string)

if (!event) {
  throw createError({ statusCode: 404, statusMessage: 'Événement non trouvé' })
}

// Fetch upcoming events for sidebar
const { data: upcomingEvents } = await useAsyncData('agenda-sidebar', () => getUpcomingEvents())

// SSR-safe: useState ensures consistent timestamp between server and client
const nowTimestamp = useState('event-detail-now', () => Date.now())

const relatedEvents = computed(() => {
  const now = nowTimestamp.value
  return upcomingEvents.value
    ?.filter(e => e.slug !== route.params.slug && new Date(e.dateRaw).getTime() > now)
    .sort((a, b) => new Date(a.dateRaw).getTime() - new Date(b.dateRaw).getTime())
    .slice(0, 2) || []
})

// Set SEO using the composable
await setEventSeo({
  title: event.title,
  description: event.description,
  metaTitle: event.metaTitle,
  metaDescription: event.metaDescription,
  keywords: event.keywords,
  ogImage: event.ogImage,
  image: event.image,
  date: event.fullDate
})

const { siteUrl, setStructuredData } = useStructuredData()
setStructuredData(buildEventSchema(siteUrl, {
  title: event.title,
  description: event.metaDescription || event.description,
  dateRaw: event.dateRaw,
  dateEndRaw: event.dateEndRaw,
  location: event.location,
  image: event.ogImage || event.image,
  url: buildCanonicalUrl(siteUrl, route.path)
}))

// Registration logic
const requiresRegistration = computed(() => event.requiresRegistration ?? false)

const isRegistrationClosed = computed(() => {
  if (!requiresRegistration.value) return false
  const deadline = event.registrationDeadline || event.date
  return new Date(deadline).getTime() < nowTimestamp.value
})

const isFull = computed(() => {
  if (!requiresRegistration.value) return false
  if (!event.maxRegistrations || event.currentRegistrations === undefined) return false
  return event.currentRegistrations >= event.maxRegistrations
})
</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <!-- Hero Image -->
    <UiPageHero 
      :image="event.image" 
      :title="event.title"
      height="screen"
      align="center"
    >
      <template #badge>
        <UiBadge variant="primary">
          {{ event.type }}
        </UiBadge>
      </template>

      <template #info>
        <div class="flex flex-wrap items-center justify-center gap-8 text-white text-xs font-bold uppercase tracking-widest">
          <span class="flex items-center gap-2">
            <i class="far fa-calendar-alt text-oxo-red" aria-hidden="true"></i> 
            <span v-if="event.fullEndDate" class="uppercase">Du {{ event.fullDate }} au {{ event.fullEndDate }}</span>
            <span v-else>{{ event.fullDate }}</span>
          </span>
          <span class="flex items-center gap-2"><i class="far fa-clock text-oxo-red" aria-hidden="true"></i> {{ event.time }}</span>
          <a 
            v-if="event.location"
            :href="getMapsUrl(event.location)"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-2 hover:text-oxo-red transition-colors"
          >
            <i class="fas fa-map-marker-alt text-oxo-red" aria-hidden="true"></i> {{ event.location }}
          </a>
        </div>
      </template>

      <template #actions>
        <AppButton to="/actus" variant="outline" size="md">
          <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
          Retour aux actualités
        </AppButton>
      </template>
    </UiPageHero>

    <!-- Content -->
    <div class="container mx-auto px-container-px relative z-10 mb-24 py-section-py">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-7xl mx-auto">
        
        <!-- Main Content -->
        <div class="lg:col-span-8">
          <div class="bg-oxo-card-bg rounded-2xl shadow-xl p-8 md:p-12 border border-oxo-border">
            
            <!-- Description -->
            <div class="prose prose-lg dark:prose-invert max-w-none mb-12">
              <div 
                class="border-l-4 border-oxo-yellow pl-6" 
                v-html="event.description"
              ></div>
            </div>

            <!-- Calendar Info Block -->
            <div class="bg-background/50 p-6 rounded-xl border border-oxo-border">
              <h4 class="font-bold text-foreground mb-4 flex items-center gap-2">
                <i class="far fa-calendar-check text-oxo-red" aria-hidden="true"></i> Ne manquez pas l'événement
              </h4>
              <AddToCalendar 
                :title="event.title"
                :description="event.description"
                :location="event.location"
                :start-date="event.dateRaw"
                :end-date="event.dateEndRaw"
              />
            </div>

            <!-- Back Link -->
            <div class="mt-12 pt-8 border-t border-oxo-border">
              <NuxtLink to="/actus" class="text-foreground-dim hover:text-oxo-red transition-colors font-medium inline-flex items-center gap-2">
                <i class="fas fa-arrow-left" aria-hidden="true"></i> Retour aux actualités
              </NuxtLink>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <aside class="lg:col-span-4 space-y-8 sticky top-8 h-fit">
          
          <!-- Registration Status / Form -->
          <div class="bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border shadow-lg">
             <!-- Case 1: Entrée libre -->
             <div v-if="!requiresRegistration" class="text-center">
               <div class="inline-block p-4 rounded-full bg-green-500/10 text-green-500 mb-4 border border-green-500/20">
                 <i class="fas fa-door-open text-3xl" aria-hidden="true"></i>
               </div>
               <h3 class="text-xl font-bold text-foreground mb-2">Entrée libre</h3>
               <p class="text-sm text-foreground-dim">Ouvert à tous, sans inscription.</p>
             </div>

             <!-- Case 2: Inscriptions closes -->
             <div v-else-if="isRegistrationClosed" class="text-center">
               <div class="inline-block p-4 rounded-full bg-background border border-oxo-border text-foreground-dim/40 mb-4">
                 <i class="far fa-clock text-3xl" aria-hidden="true"></i>
               </div>
               <h3 class="text-xl font-bold text-foreground mb-2">Inscriptions closes</h3>
               <p class="text-sm text-foreground-dim">La date limite est dépassée.</p>
             </div>

             <!-- Case 3: Complet -->
             <div v-else-if="isFull" class="text-center">
               <div class="inline-block p-4 rounded-full bg-oxo-red/10 text-oxo-red mb-4 border border-oxo-red/20">
                 <i class="fas fa-user-slash text-3xl" aria-hidden="true"></i>
               </div>
               <h3 class="text-xl font-bold text-foreground mb-2">Complet</h3>
               <p class="text-sm text-foreground-dim">Capacité maximale atteinte.</p>
             </div>

             <!-- Case 4: Formulaire -->
             <EventRegistrationForm v-else :event-slug="route.params.slug as string" :event-name="event.title" />
          </div>

          <!-- Linked News -->
          <section v-if="event.linkedNews && event.linkedNews.length > 0" class="bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border">
            <header>
              <h3 class="font-display font-bold text-lg text-foreground mb-6 flex items-center gap-2">
                <i class="fas fa-newspaper text-oxo-red" aria-hidden="true"></i>
                Actus liée(s)
              </h3>
            </header>
            <ul class="space-y-4 list-none p-0">
              <li v-for="item in event.linkedNews" :key="item.id">
                <NuxtLink 
                  :to="`/actus/${item.slug}`"
                  class="group block p-4 rounded-xl bg-background border border-oxo-border hover:bg-oxo-card-bg/20 transition-colors"
                >
                  <span class="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-oxo-red text-oxo-red mb-2 inline-block">Actualité</span>
                  <time class="text-xs text-foreground-dim float-right">{{ item.date }}</time>
                  <h4 class="font-bold text-foreground text-sm line-clamp-2 group-hover:text-oxo-red transition-colors clear-both">
                    {{ item.title }}
                  </h4>
                </NuxtLink>
              </li>
            </ul>
          </section>

          <!-- Linked Events -->
          <section v-if="relatedEvents.length > 0" class="bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border">
            <header>
              <h3 class="font-display font-bold text-lg text-foreground mb-6 flex items-center gap-2">
                <i class="fas fa-calendar-alt text-oxo-red" aria-hidden="true"></i>
                À lire aussi
              </h3>
            </header>
            <ul class="space-y-4 list-none p-0">
              <li v-for="relEvent in relatedEvents" :key="relEvent.id">
                <NuxtLink 
                  :to="`/actus/evenement/${relEvent.slug}`"
                  class="group block p-4 rounded-xl bg-background border border-oxo-border hover:bg-oxo-card-bg/20 transition-colors"
                >
                  <div class="flex items-center gap-2 mb-2">
                    <span class="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-oxo-red text-oxo-red">{{ relEvent.type }}</span>
                    <time class="text-oxo-yellow font-bold text-xs">{{ relEvent.date }}</time>
                  </div>
                  <h4 class="font-bold text-foreground text-sm line-clamp-2 group-hover:text-oxo-red transition-colors">
                    {{ relEvent.title }}
                  </h4>
                </NuxtLink>
              </li>
            </ul>
          </section>

          <!-- Discover OXO -->
          <nav class="bg-linear-to-br from-oxo-red to-red-600 rounded-2xl p-6 text-white">
            <h3 class="font-display font-bold text-lg mb-4">Découvrir OXO</h3>
            <ul class="space-y-3 list-none p-0">
              <li>
                <NuxtLink to="/formations" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                  <i class="fas fa-graduation-cap w-5" aria-hidden="true"></i>
                  Nos formations
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/projets" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                  <i class="fas fa-gamepad w-5" aria-hidden="true"></i>
                  Projets étudiants
                </NuxtLink>
              </li>
              <li>
                <NuxtLink to="/candidature" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                  <i class="fas fa-paper-plane w-5" aria-hidden="true"></i>
                  Postuler
                </NuxtLink>
              </li>
            </ul>
          </nav>

        </aside>
      </div>
    </div>

    <AppCTA
      title="Une question"
      titleHighlight="SUR L'ÉVÉNEMENT ?"
      subtitle="Notre équipe est là pour vous répondre."
      buttonText="Nous contacter"
      buttonLink="/contact"
    />
  </div>
</template>
